import React, { useState, useEffect, useCallback } from 'react';
import { X, Radio, Eye, ShoppingCart, CreditCard, MousePointer, ArrowRight, Zap, MapPin, Globe, Users } from 'lucide-react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const EVENT_LABELS = {
  page_view: { label: 'Viewing', color: 'text-blue-600', bg: 'bg-blue-50', icon: Eye },
  product_viewed: { label: 'Product View', color: 'text-indigo-600', bg: 'bg-indigo-50', icon: MousePointer },
  add_to_cart: { label: 'Added to Cart', color: 'text-amber-600', bg: 'bg-amber-50', icon: ShoppingCart },
  checkout_started: { label: 'Checkout', color: 'text-orange-600', bg: 'bg-orange-50', icon: CreditCard },
  purchase: { label: 'Purchased!', color: 'text-green-600', bg: 'bg-green-50', icon: Zap },
};

export default function RealtimeModal({ isOpen, onClose }) {
  const [data, setData] = useState(null);
  const brandId = localStorage.getItem('currentBrandId') || '';
  const brandName = localStorage.getItem('currentBrandName') || 'Brand';

  const fetchData = useCallback(async () => {
    if (!brandId) return;
    try {
      const res = await fetch(`https://api.glowytics.com/api/pixel/realtime?brandId=${brandId}`, { credentials: 'include' });
      if (!res.ok) return;
      setData(await res.json());
    } catch (e) {}
  }, [brandId]);

  useEffect(() => {
    if (!isOpen) return;
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [isOpen, fetchData]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const av = data?.activeVisitors || 0;

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logoicon.png" alt="Glowytics" className="w-7 h-7 rounded-lg" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-semibold text-gray-900">{brandName}</h1>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-50 border border-green-200">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span className="text-green-700 text-[10px] font-semibold uppercase tracking-wider">Live</span>
                </span>
              </div>
              <p className="text-xs text-gray-400">
                Realtime · Last 5 min · {data?.timestamp ? new Date(data.timestamp).toLocaleTimeString() : '...'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Top row: Big number + Funnel badges */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
          {/* Visitors online */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col items-center justify-center">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-5 h-5 text-blue-500" />
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Online Now</span>
            </div>
            <span className="text-5xl font-bold text-gray-900 tabular-nums">{av}</span>
            <span className="text-xs text-gray-400 mt-1">{av === 1 ? 'visitor' : 'visitors'}</span>
          </div>

          {/* Funnel badges */}
          <FunnelBadge icon={Eye} label="Page Views" value={data?.pageViews5min || 0} color="blue" />
          <FunnelBadge icon={MousePointer} label="Product Views" value={data?.productViews5min || 0} color="indigo" />
          <FunnelBadge icon={ShoppingCart} label="Add to Cart" value={data?.addToCarts5min || 0} color="amber" />
          <FunnelBadge icon={CreditCard} label="Checkout" value={data?.checkouts5min || 0} color="orange" />
          <FunnelBadge icon={Zap} label="Purchase" value={data?.purchases5min || 0} color="green" />
        </div>

        {/* Map */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden relative" style={{ height: '360px' }}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={getMapConfig(data?.activeLocations)}
            style={{ width: '100%', height: '100%' }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rpiKey || geo.properties.name}
                    geography={geo}
                    fill="#e5e7eb"
                    stroke="#d1d5db"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { fill: '#d1d5db', outline: 'none' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))
              }
            </Geographies>
            {(data?.activeLocations || []).map((loc, i) => (
              <Marker key={i} coordinates={[parseFloat(loc.longitude), parseFloat(loc.latitude)]}>
                <circle r={6} fill="#3b82f6" fillOpacity={0.9} stroke="#2563eb" strokeWidth={1.5} />
                <circle r={12} fill="#3b82f6" fillOpacity={0.15}>
                  <animate attributeName="r" from="6" to="18" dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.3" to="0" dur="2s" repeatCount="indefinite" />
                </circle>
              </Marker>
            ))}
          </ComposableMap>

          {/* Country badges */}
          {data?.countryBreakdown && data.countryBreakdown.length > 0 && (
            <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
              {data.countryBreakdown.slice(0, 8).map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200 text-xs text-gray-700 shadow-sm">
                  <MapPin className="w-3 h-3 text-blue-500" />
                  {c.country} <span className="font-semibold text-gray-900">{c.visitors}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Bottom row: Active Pages + Live Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Active Pages */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-4 h-4 text-blue-500" />
              <h3 className="text-sm font-semibold text-gray-800">Active Pages</h3>
              <span className="ml-auto text-xs text-gray-400">{(data?.topPages || []).length} pages</span>
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {(data?.topPages || []).map((page, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-50 text-blue-600 text-xs font-bold flex-shrink-0">
                    {parseInt(page.visitors)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-700 truncate group-hover:text-gray-900 transition-colors">
                      {page.page_path}
                    </p>
                    {page.page_title && (
                      <p className="text-xs text-gray-400 truncate">{page.page_title}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{page.views} views</span>
                </div>
              ))}
              {(!data?.topPages || data.topPages.length === 0) && (
                <div className="text-center py-8 text-gray-400 text-sm">No active pages</div>
              )}
            </div>
          </div>

          {/* Live Feed */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="w-4 h-4 text-green-500" />
              <h3 className="text-sm font-semibold text-gray-800">Live Feed</h3>
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {(data?.recentEvents || []).map((evt, i) => {
                const evtInfo = EVENT_LABELS[evt.event] || { label: evt.event, color: 'text-gray-500', bg: 'bg-gray-50', icon: ArrowRight };
                const Icon = evtInfo.icon;
                const timeAgo = getTimeAgo(evt.event_timestamp);
                return (
                  <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={`p-1.5 rounded-lg ${evtInfo.bg} flex-shrink-0 mt-0.5`}>
                      <Icon className={`w-3.5 h-3.5 ${evtInfo.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold ${evtInfo.color}`}>{evtInfo.label}</span>
                        {evt.city && evt.country && (
                          <span className="text-[10px] text-gray-400">{evt.city}, {evt.country}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{evt.page_path}</p>
                    </div>
                    <span className="text-[10px] text-gray-400 flex-shrink-0 mt-1">{timeAgo}</span>
                  </div>
                );
              })}
              {(!data?.recentEvents || data.recentEvents.length === 0) && (
                <div className="text-center py-8 text-gray-400 text-sm">Waiting for events...</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-1.5 py-3">
          <img src="/logoicon.png" alt="" className="w-3.5 h-3.5 rounded" />
          <span className="text-xs text-gray-400">Powered by <span className="text-gray-600 font-medium">Glowytics</span></span>
        </div>
      </div>
    </div>
  );
}

function FunnelBadge({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    green: 'bg-green-50 text-green-600 border-green-100',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col items-center justify-center">
      <div className={`p-2 rounded-lg ${colors[color]} mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

function getTimeAgo(timestamp) {
  if (!timestamp) return '';
  const diff = (Date.now() - new Date(timestamp).getTime()) / 1000;
  if (diff < 10) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  return `${Math.floor(diff / 60)}m ago`;
}

function getMapConfig(activeLocations) {
  const DEFAULT = { scale: 800, center: [35, 39] };
  if (!activeLocations || activeLocations.length === 0) return DEFAULT;
  const lats = activeLocations.map(l => parseFloat(l.latitude)).filter(v => !isNaN(v));
  const lngs = activeLocations.map(l => parseFloat(l.longitude)).filter(v => !isNaN(v));
  if (lats.length === 0) return DEFAULT;
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const maxSpread = Math.max(maxLat - minLat, maxLng - minLng);
  let scale;
  if (maxSpread < 1) scale = 2500;
  else if (maxSpread < 5) scale = 1200;
  else if (maxSpread < 15) scale = 600;
  else if (maxSpread < 40) scale = 350;
  else if (maxSpread < 80) scale = 200;
  else scale = 140;
  return { scale, center: [(minLng + maxLng) / 2, (minLat + maxLat) / 2] };
}
